import { SEO, pageSEO } from '../components/shared/SEO';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Target, Users, Lightbulb, CheckCircle, ArrowRight,
  Search, PenTool, Code, TestTube2, HeadphonesIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';
import { ProcessFlow } from '../components/shared/ProcessFlow';
import { Stats } from '../components/shared/Stats';

const values = [
  {
    icon: Target,
    title: 'Client-Centric Approach',
    description: 'Every solution we deliver is tailored to your unique business needs and objectives.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation First',
    description: 'We leverage the latest Guidewire capabilities and industry best practices.',
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: '500+ certified Guidewire professionals with deep domain expertise.',
  },
  {
    icon: CheckCircle,
    title: 'Quality Assured',
    description: 'Rigorous QA processes ensure reliable, high-performance solutions.',
  },
];

const deliveryLifecycle = [
  {
    icon: Search,
    title: 'Discover',
    description: 'We analyze your current systems, processes, and business requirements to create a comprehensive roadmap.',
  },
  {
    icon: PenTool,
    title: 'Design',
    description: 'Our architects design scalable solutions aligned with Guidewire best practices and your enterprise goals.',
  },
  {
    icon: Code,
    title: 'Implement',
    description: 'Agile delivery teams build and configure your Guidewire platform with precision and efficiency.',
  },
  {
    icon: TestTube2,
    title: 'QA',
    description: 'Comprehensive testing ensures quality, performance, and seamless integration with your ecosystem.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Support',
    description: 'Ongoing maintenance, optimization, and 24/7 support to keep your systems running smoothly.',
  },
];

const teamMembers = [
  { name: 'Sarah Chen', role: 'CEO & Founder', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop' },
  { name: 'Michael Roberts', role: 'CTO', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop' },
  { name: 'Emily Johnson', role: 'VP of Delivery', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop' },
  { name: 'David Park', role: 'Head of Engineering', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop' },
  { name: 'Jessica Williams', role: 'Director of QA', image: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400&h=400&fit=crop' },
  { name: 'Robert Martinez', role: 'Cloud Architect', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop' },
  { name: 'Amanda Lee', role: 'Principal Consultant', image: 'https://images.unsplash.com/photo-1598550874175-4d0ef436c909?w=400&h=400&fit=crop' },
  { name: 'James Wilson', role: 'Solutions Architect', image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop' },
];

const officeImages = [
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1497215842964-222b430dc094?w=600&h=400&fit=crop',
];

const About = () => {
  return (
    <div data-testid="about-page">
      <SEO {...pageSEO.about} />
      <PageHeader
        label="About Guidepro"
        title="Empowering Insurers with Scalable, Future-Ready Guidewire Ecosystems"
        description="Since 2010, we've been helping insurance companies transform their operations with cutting-edge Guidewire solutions and unmatched expertise."
      />

      {/* Mission Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-6">
                Our Mission
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">
                Accelerating Digital Transformation in Insurance
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                At Guidepro, we believe that modern insurers need more than just technology — 
                they need a strategic partner who understands the complexities of insurance 
                operations and can deliver solutions that drive real business value.
              </p>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                Our mission is to empower insurance companies with scalable, future-ready 
                Guidewire ecosystems that streamline operations, enhance customer experience, 
                and accelerate growth.
              </p>
              <div className="space-y-3">
                {[
                  'API-first architecture for seamless integrations',
                  'Cloud-native solutions optimized for scale',
                  'Agile delivery with continuous improvement',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-sky-500 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              <div className="relative">
                <div className="absolute inset-0 bg-sky-100 rounded-3xl rotate-3 scale-105" />
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Guidepro Team"
                  className="relative rounded-2xl shadow-xl w-full h-auto"
                  data-testid="about-team-image"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50 dark:bg-slate-800 border-y border-slate-100 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stats />
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-4">
              Why Guidepro
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              What Sets Us Apart
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              We bring together deep Guidewire expertise, industry knowledge, and a 
              commitment to excellence that drives measurable results.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <StaggerItem key={index}>
                <div className="text-center p-8 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-white dark:hover:bg-slate-700 hover:shadow-xl transition-all duration-300 border border-transparent hover:border-slate-100 dark:hover:border-slate-600">
                  <div className="w-16 h-16 rounded-2xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-sky-600 dark:text-sky-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Delivery Lifecycle */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-slate-50 to-sky-50 dark:from-slate-800 dark:to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-4">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Delivery Lifecycle
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Our proven methodology ensures consistent, high-quality delivery 
              across all engagements.
            </p>
          </AnimatedSection>

          <ProcessFlow steps={deliveryLifecycle} />
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-4">
              Leadership
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Meet Our Team
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Industry veterans with decades of combined experience in insurance 
              technology and Guidewire implementations.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
            {teamMembers.map((member, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -8 }}
                  className="text-center"
                  data-testid={`team-member-${index}`}
                >
                  <div className="relative mb-4 overflow-hidden rounded-2xl">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <h4 className="text-base lg:text-lg font-bold text-slate-900 dark:text-white mb-1">
                    {member.name}
                  </h4>
                  <p className="text-sky-600 dark:text-sky-400 font-medium text-sm">
                    {member.role}
                  </p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Office Gallery */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-4">
              Our Workspace
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Where Innovation Happens
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Modern, collaborative spaces designed to foster creativity and deliver excellence.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {officeImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative overflow-hidden rounded-2xl ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
              >
                <motion.img
                  src={image}
                  alt={`Office space ${index + 1}`}
                  className={`w-full object-cover ${index === 0 ? 'h-64 lg:h-full' : 'h-48 lg:h-56'}`}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Partner with Guidepro?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Let's discuss how we can help you achieve your Guidewire implementation 
              goals and transform your insurance operations.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-8"
                >
                  Get in Touch
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-800 h-14 px-8"
                >
                  Explore Services
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default About;
